import { appContext } from "../../../../AppContext";
import { RouterProps } from "react-router";
import Booking from "../../../OneLens/Shared/Home/homeBookings.png";
import Preferences from "./homePreferences.png";
import Visitors from "../../../OneLens/Shared/Home/homeVisitor.png";
import Space from "../../../OneLens/Shared/Home/homeBookSpace.png";
import { Box, Divider, Grid, SelectChangeEvent } from "@mui/material";
import { Typography } from "@mui/material";
import IbssHomeCard from "../../../../Components/Cards/HomePageCard/IbssHomeCard";
import { IbssPage } from "../../../../Components/Core/BasePage/IbssPage";
import { SearchSpacesHelper } from "../../../Shared/Spaces/Search/SearchSpacesHelper";
import IbssFormControl from "../../../../Components/Forms/FormControl/IbssFormControl";
import IbssInputDropDown from "../../../../Components/Inputs/SelectList/IbssInputDropDown";
import Helper, { getBuildingNameUsingBuildingId } from "../../../../Common/Helper";
import IbssQuickLinkCard from "../../../../Components/Cards/SignPostCard/IbssQuickLinkCard";
import { DateHelper } from "../../../../Common/DateHelper";
import NewsAndEvents, { AnnouncementView, IEvent } from "./NewsAndEvents";
import { SignPost } from "../../../../Providers.Api/ApiCacheDataModels";
import { QueryParams } from "./QueryParams";
import { ApplicationType } from "../../../Admin/Signposts/List/DataModels";
import { rights } from "../../../../Common/RightsManager";
import IbssPageHeader from "../../../../Components/Headings/PageHeading/IbssPageHeader";
import IbssNoEventCard from "../../../../Components/Cards/EventCard/IbssNoEventCard";
import Events from "./EventsNotification.svg";
import { BookingParties, TicketedEvent } from "./DataModels";
import { PagedResponse } from "../../../../Providers.Api/Models";

class FlexHome extends IbssPage<IProps, IState, QueryParams>
{
    private get appState() { return appContext().state; }
    private get local() { return appContext().localStorageProvider; }
    private get labels() { return appContext().labels; }
    private get apiCache() { return appContext().apiCache };
    private userCanRead: boolean;
    private userPrefs = appContext().localStorageProvider.getUserPreferences();
    private hasViewEvents = rights.FLEX.Events.View.has();
    private userHasEventReadRights = rights.DATAMODEL.TicketedEvents.Read.has();
    private userHasNewsReadRights = rights.DATAMODEL.Notifications.Read.has();

    constructor(props: IProps)
    {
        super(props, new QueryParams());
        this.userCanRead = rights.DATAMODEL.BuildingKPIs.Read.has();
        this.state =
        {
            selectedBuildingId: this.userPrefs.SearchPrefs.DefaultBuilding ?? 1,
            signposts: [],
            isLoadingQuickLinks: false,
            buildingOptions: [],
            eventsData: [],
            newsData: [],
            isLoadingNews: false,
            isLoadingEvents: false,
        };
    }

    public async componentDidMount(): Promise<void>
    {
        this.pageTitle = this.labels.HubMenuHome;
        this.loadBuildings();
    }

    public async queryParamsDidUpdate(): Promise<void>
    {
        const defaultBuildingId = this.userPrefs.SearchPrefs.DefaultBuilding ?? this.state.buildingOptions[0].value;

        if (!this.queryParams.building)
        {
            return this.pushQueryParams({ building: defaultBuildingId }, true)
        }
        await this.setStateAsync({
            selectedBuildingId: this.queryParams.building 
        });
        this.userHasNewsReadRights && await this.loadActiveNewsByNodeId();
        this.userHasEventReadRights && await this.loadEventsByNodeId();
        this.userCanRead && await this.fetchSignposts();
    }

    private async fetchSignposts(): Promise<void>
    {
        if (this.state.isLoadingQuickLinks)
        {
            return;
        }
        
        this.setState({ isLoadingQuickLinks: true });
        try
        {
            const signpostList = await this.apiCache.getQuickLinkByBuilding(this.state.selectedBuildingId);
            const activeSignposts = this.filterActiveQuickLinks(signpostList)
            this.setState({ signposts: activeSignposts });
        }
        finally
        {
            this.setState({ isLoadingQuickLinks: false });
        }
    }

    private async loadActiveNewsByNodeId(): Promise<void>
    {
        this.setState({ isLoadingNews: true, newsData: [] });
        if (!this.state.selectedBuildingId)
        {
            this.setState({ newsData: [], isLoadingNews: false });
            return;
        }
        try
        {
            const newsNodeId = this.state.selectedBuildingId;
            const response = await this.apiCache.getActiveNotificationsByNodeId(newsNodeId);
            const activeNotifications = response.value.map((news) => AnnouncementView.fromAnnouncementToView(news));           
            const latestThreeNews = activeNotifications.toSorted((a, b) => (b.availableFrom.toMillis() - a.availableFrom.toMillis())).slice(0, 3);
            this.setState({ newsData: latestThreeNews });
        }
        finally
        {
            this.setState({ isLoadingNews: false });
        }
    }

    private async loadEventsByNodeId(): Promise<void>
    {
        this.setState({ eventsData: [], isLoadingEvents: true })
        if (!this.state.selectedBuildingId)
        {
            this.setState({ eventsData: [] });
            return;
        }
        try
        {
            const eventNodeId = this.state.selectedBuildingId;
            let skipToken: string | null = '';
            let allEvents: TicketedEvent[] = [];
            do 
            {
            const ticketedEventResponse: PagedResponse<TicketedEvent[]> = 
            await appContext().ibssApiClientV2.v2.byNodeid.ticketedevents.get<PagedResponse<TicketedEvent[]>>({
                nodeId: eventNodeId,
                select: TicketedEvent,
                recursive: true,
                suppressErrorPopup: true,
                skipToken: skipToken || ''
            });

            if (ticketedEventResponse?.value?.length > 0) 
            {
                allEvents = allEvents.concat(ticketedEventResponse.value);
            }

            skipToken = ticketedEventResponse.skipToken || null;

            } while (skipToken);

            if (allEvents.length === 0)
            {
                this.setState({ eventsData: [] });
                return;
            }
            const latestThreeEvents = allEvents
                .filter((event) => event.Event_Date && DateHelper.now(this.state.selectedBuildingId) < event.Event_Date.plus({ days: 1 }).startOf('day'))
                .sort((a, b) => (b.Event_Date.toMillis() - a.Event_Date.toMillis()))
                .slice(0, 3)

            const eventBookingIds = latestThreeEvents.map((event) => ({
                bookingId: event.Booking_Id
            }));

            const filterEventBookingsIds = eventBookingIds.map(ticketedEvent => `Booking_Id eq '${ticketedEvent.bookingId}'`).join(' or ');
            let bookingPartiesPerEventBookingIds: PagedResponse<BookingParties[]> | null = null;

            if (filterEventBookingsIds.length > 0)
            {
                bookingPartiesPerEventBookingIds = await appContext().ibssApiClientV2.v2.byNodeid.bookingparties.get<PagedResponse<BookingParties[]>>(
                    {
                        nodeId: eventNodeId,
                        recursive: true,
                        select: BookingParties,
                        suppressErrorPopup: true,
                        filter: `(${filterEventBookingsIds}) and Booking_Participant_Status eq '1' and (Booking_Participant_Type eq 1 or Booking_Participant_Type eq 4 or Booking_Participant_Type eq 2)`,  //booking participant type -> 1 : attendee, 4 : owner , 2 : visitor
                        skipToken: ''
                    }
                ).catch(() => null);
            }

            const spaceList = await this.apiCache.getSpacesByBuilding(eventNodeId);
            const eventsData = latestThreeEvents.map((event) =>
            {
                let eventAttendeesCount = 0;
                let isUserAttending = false;
                if (bookingPartiesPerEventBookingIds?.value != null)
                {
                    eventAttendeesCount = bookingPartiesPerEventBookingIds.value.filter((b) => b.Booking_Id === event.Booking_Id).length
                    const currentUser = this.local.getUserDetails();
                    if (bookingPartiesPerEventBookingIds.value.some((b) => b.Booking_Participant_Email === currentUser.email && b.Booking_Id === event.Booking_Id))
                    {
                        isUserAttending = true;
                    }
                }

                const space = spaceList.find(x => x.Space_Id === event.Space_Id);
                const spaceName = space ? space.Space_Name : '';
                return ({
                    ticketedEventId: event.Ticketed_Event_Id,
                    nodeId: event.Node_Id,
                    bookingId: event.Booking_Id,
                    eventTitle: event.Event_Title,
                    eventShortDescription: event.Event_Short_Description,
                    eventDescription: event.Event_Description,
                    webSiteUrl: event.WebSite_URL,
                    imageUri: event.ImageURI,
                    eventDate: event.Event_Date,
                    eventStart: event.Event_Start_DateTime,
                    eventEnd: event.Event_End_DateTime,
                    eventCapacity: event.Event_Capacity,
                    isWaitingListAllowed: event.IsWaitingList_Allowed,
                    invitePermission: event.Invite_Permission,
                    eventStatus: event.Event_Status,
                    eventAttendees: eventAttendeesCount,
                    userIsAttending: isUserAttending,
                    spaceName: spaceName,
                }) as IEvent;
            });
            this.setState({ eventsData: eventsData });
        }
        finally
        {
            this.setState({ isLoadingEvents: false });
        }
    }

    private filterActiveQuickLinks(signposts: SignPost[]): SignPost[]
    {
        return signposts.filter((signpost) =>
            signpost.Application.includes(ApplicationType.flex)
        );
    }   

    private getLatestQuickLinks(signposts: SignPost[]): SignPost[]
    {
        const filterPosts = signposts
            .sort((a, b) =>
            {
                return (b.BuildingKPI_Order || 0) - (a.BuildingKPI_Order || 0);
            })
            .slice(0, 4);

        return filterPosts; 
    }

    private loadBuildings(): void
    {
        const buildings = Helper.getAllBuildingsData().sort((a, b) => a.Name.localeCompare(b.Name));
        const options = buildings.map(i => ({ label: i.Name, value: i.Node_Id }));
        this.setState({ buildingOptions: options });
    }


    private handleBuildingChange(event:SelectChangeEvent<number>) :void
    {
        const buildingId = Number(event.target.value)
        this.pushQueryParams({ building: buildingId });
    }

    private renderLoadingState(): JSX.Element
    {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" sx={{ minHeight: 150, width: "100%" }} p={3}>
                <Typography variant="body1">
                    {this.labels.HubLabelLoadingText}
                </Typography>
            </Box>
        );
    }

    private renderQuickLinkCard(signpost: SignPost): JSX.Element 
    {
        function getDestinationUrl(signpost: SignPost)
        {
            if (signpost.Application.includes(ApplicationType.flex))
            {
                return signpost.Destination.Web || null;
            }
            if (signpost.Application.includes(ApplicationType.roamer))
            {
                return signpost.Destination.Android || signpost.Destination.iOS || null;
            }
            return null;
        }

        return (
            <Grid item xs={12} sm={6} lg={4} key={signpost.ShortcutId}>
                <IbssQuickLinkCard
                    title={signpost.Heading}
                    subtitle={signpost.SubHeading}
                    additionalInfo={getBuildingNameUsingBuildingId(signpost.Node_Id)}
                    buttonLabel={signpost.Label}
                    iconSrc={signpost.Image}
                    destinationUrl={getDestinationUrl(signpost)}
                />
            </Grid>
        );
    }

    private renderQuickLinks(): JSX.Element
    {
        if (this.state.isLoadingQuickLinks)
        {
            return this.renderLoadingState();
        }

        const latestQuickLinks = this.getLatestQuickLinks(this.state.signposts);
        return <>{latestQuickLinks.map(signpost => this.renderQuickLinkCard(signpost))}</>;
    }


    private navigateToDetails(nodeId: number, eventId: string): void 
    {
        this.props.history.push(`/flex/events/${eventId}/${nodeId}`)
    }

    private navigateToRoute(path: string): void
    {
        this.props.history.push(path);
    }


    public render(): JSX.Element
    {
        const displayName = this.local.getUserDetails().displayName;

        const callToActions: ICallToAction[] = [
            {
                id: '1',
                title: this.labels.funcBookingHomeSpace_S,
                description: this.labels.funcBookingHomeSpace_L,
                src: Space,
                imageAlt: this.labels.funcBookingHomeSpace_S,
                route: `/flex/spaces/search?${SearchSpacesHelper.buildQuery()}`,
                permissions: rights.FLEX.Search.Spaces.has()
            },

            {
                id: '2',
                title: this.labels.HubLabelBookings,
                description: this.labels.funcBookingHomeYourView_L,
                src: Booking,
                imageAlt: this.labels.HubLabelBookings,
                route: `/flex-my-bookings`,
                permissions: rights.FLEX.MySchedule.MyBookings.has(),
            },

            {
                id: '3',
                title: this.labels.HubMenuVisitors,
                description: this.labels.funcBookingHomeYourVisitors_L,
                src: Visitors,
                imageAlt: this.labels.HubMenuVisitors,
                route: `/flex-my-visitor`,
                permissions: rights.FLEX.MySchedule.MyVisitors.has(),

            },

            {
                id: '4',
                title: this.labels.funcBookingHomePreferences_S,
                description: this.labels.funcBookingHomePreferences_L,
                src: Preferences,
                imageAlt: this.labels.funcBookingHomePreferences_S,
                route: `/flex-user-pref-notifications`,
                permissions: rights.FLEX.UserPreferences.has(),
            },
        ];

        return (
            <div className="page-height-exct-header">
                <div className="rightPanel-main-content">
                    <Box
                        sx={theme =>
                        ({
                            [theme.breakpoints.up(1800)]: { // media query for screen sizes above 1800 pixels width
                                mt: 4,
                                mb: 4,
                            },
                            [theme.breakpoints.down(1800)]: {
                                mt: 0,
                                mb: 2,
                            },
                        })
                        }
                    >
                        <IbssPageHeader
                            title={`${this.labels.HubLabelWelcomeText}, ${displayName}`}
                        />
                    </Box>
                    <Box>
                        <Grid container spacing={3}>
                            {callToActions.filter(i => i.permissions === true).map((item) =>
                                <Grid item md={6}  key={item.id}>
                                    <IbssHomeCard
                                        title={item.title}
                                        description={item.description}
                                        src={item.src}
                                        imageAlt={item.imageAlt}
                                        route={item.route}
                                        redirect={() => this.props.history.push(item.route)}
                                    />
                                </Grid>
                            )}
                        </Grid>
                    </Box>
                    <Box>
                    {(this.hasViewEvents||this.userHasEventReadRights||this.userHasNewsReadRights||this.userCanRead) &&
                        <Box>
                            <Box my={3}>
                                <Divider />
                            </Box>
                            <Box display={'flex'}  my={3}  justifyContent={"flex-end"}>
                                <Box display={'flex'} gap={2}>
                                    <IbssFormControl fullWidth={true} sx={{ backgroundColor: (theme) => theme.palette.background.paper }}>
                                        <IbssInputDropDown
                                            id="buildingSelection"
                                            inputLabel={this.labels.HubLabelBuilding}
                                            fullWidth={true}
                                            minWidth={200}
                                            options={this.state.buildingOptions}
                                            value={this.state.selectedBuildingId}
                                            onChange={(e: SelectChangeEvent<number>) => this.handleBuildingChange(e)}
                                        />
                                    </IbssFormControl>
                                </Box>
                            </Box>
                        {((this.state.newsData.length===0 && this.state.eventsData.length===0 && this.state.signposts.length===0) && !(this.state.isLoadingEvents || this.state.isLoadingNews || this.state.isLoadingQuickLinks)) && <IbssNoEventCard
                            title={this.labels.funcNoNewsEventsQuicklinksFoundSelectedBuilding_S}
                            image={Events}
                        />}
                        </Box>}
                        <NewsAndEvents
                            navigateToRoute={(path: string) => this.navigateToRoute(path)}
                            navigateToDetails={(nodeId: number, eventId: string) => this.navigateToDetails(nodeId, eventId)}
                            newsData={this.state.newsData}
                            eventsData={this.state.eventsData}
                            isLoadingNews={this.state.isLoadingNews}
                            isLoadingEvents={this.state.isLoadingEvents}
                        />
                    </Box>
                    {(this.userCanRead && this.state.signposts.length>0) &&
                    <Box>
                        <Box display={"flex"} justifyContent={"space-between"} my={3}>
                        <Box display={"flex"} alignItems="center">
                        <IbssPageHeader
                            title={this.labels.funcQuicKLink_S}
                        />
                        </Box>

                        </Box>
                        <Grid container spacing={3}>
                            {this.renderQuickLinks()}
                        </Grid>
                    </Box>
                    }

                </div>
            </div>
        );
    }
}

export interface IProps extends RouterProps { }

export interface IState
{
    selectedBuildingId: number;
    signposts: SignPost[];
    isLoadingQuickLinks: boolean;
    buildingOptions: IListOption<number>[];
    eventsData: IEvent[];
    isLoadingNews: boolean;
    isLoadingEvents: boolean;
    newsData: AnnouncementView[];
}

export interface IListOption<TValue>
{
    label: string,
    value: TValue,
}

export interface ICallToAction
{
    id: string;
    title: string;
    description: string;
    src: string;
    imageAlt: string;
    route: string;
    permissions: boolean;
}

export default FlexHome;
